import { useState, useContext, useRef } from "react"
import cc from "classcat"
import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"
import { useForm } from "react-hook-form"
import { StaticImage } from "gatsby-plugin-image"

import {
  Input,
  SubmitButton,
  PrivacyPolicyLink,
  Switch,
} from "components/atoms/form"
import { Modal } from "components/atoms/common/Modal"
import { getEuReports } from "utils/forms"
import { siteCountry } from "utils/env"
import { ReportCard } from "components/molecules/ReportCard"
import Altcha from "components/atoms/Altcha"
import {
  EU_REPORT_2022_TAG,
  EU_REPORT_2023_TAG,
  EU_REPORT_2024_TAG,
} from "utils/constants"
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline"
import countries from "../../../../config/i18n"
import { Report2024Cover } from "./Report2024Cover"
import { Report2023Cover } from "./Report2023Cover"
import { Report2022Cover } from "./Report2022Cover"
import ContactInfoKaisa from "./ContactInfoKaisa"

const locales = countries[siteCountry] || countries.fi

const SubscribeForm = ({ setShowModal }) => {
  const {
    report2024DownloadTitle,
    report2023DownloadTitle,
    report2022DownloadTitle,
    report2023DownloadDescriptionShort,
    report2024DownloadDescriptionShort,
    report2022DownloadDescriptionShort,
  } = useTranslations()
  const reports = [
    {
      title: report2024DownloadTitle,
      id: EU_REPORT_2024_TAG,
      description: report2024DownloadDescriptionShort,
      link: "/blog/emergency-unit-annual-report-2024",
      Cover: Report2024Cover,
    },
    {
      title: report2023DownloadTitle,
      id: EU_REPORT_2023_TAG,
      description: report2023DownloadDescriptionShort,
      link: "/blog/emergency-unit-annual-report-2023",
      Cover: Report2023Cover,
    },
    {
      title: report2022DownloadTitle,
      id: EU_REPORT_2022_TAG,
      description: report2022DownloadDescriptionShort,
      link: "/blog/emergency-unit-annual-report-2022",
      Cover: Report2022Cover,
    },
  ]
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm()

  const {
    formFieldName,
    formFieldEmail,
    formErrorGeneral,
    formErrorUserExist,
    formErrorContactUs,
    subscribeHeaderSchool,
    reportDownloadButton,
    reportsDownloadButton,
    formFieldEntityTitle,
  } = useTranslations()

  const { locale } = useContext(LocaleContext)
  const localeConfig = locales[locale] || Object.values(locales)[0] || {}

  const [isSubmitError, setSubmitError] = useState(false)
  const [selectedReportIds, setSelectedReportIds] = useState([
    EU_REPORT_2024_TAG,
  ])
  const [errMessage, setErrMessage] = useState(formErrorGeneral)
  const altchaRef = useRef<{ value: string | null }>(null)

  const onSubmit = async (data) => {
    try {
      let res
      const altcha = altchaRef.current?.value
      // if (process.env.NODE_ENV === "development") {
      //   res = { status: 200 }
      // } else {
      res = await getEuReports(data, locale, selectedReportIds, altcha)
      // }

      if (res.status === 200) {
        setShowModal(true)
        reset({
          name: "",
          email: "",
          title: "",
          somevahti: false,
        })
        setSubmitError(false)
      }
    } catch ({ response }) {
      if (response?.data?.title === "Invalid Resource") {
        setErrMessage(response.data.detail)
      } else if (response?.data?.title === "Member Exists") {
        setErrMessage(formErrorUserExist)
      }
      setSubmitError(true)
    }
  }

  const handleReportChange = (reportId) => {
    const updatedIds = selectedReportIds.includes(reportId)
      ? selectedReportIds.filter((id) => id !== reportId)
      : [...selectedReportIds, reportId]
    setSelectedReportIds(updatedIds)
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mb-0 space-y-8 rounded-lg bg-white px-4 py-4"
    >
      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        {reports.map((report, index) => (
          <ReportCard
            {...report}
            key={index}
            CoverImage={report.Cover}
            onClick={() => handleReportChange(report.id)}
            checked={selectedReportIds.includes(report.id)}
          />
        ))}
      </div>
      <div className="col-span-3 space-y-4">
        <Input
          label={formFieldName}
          name="name"
          control={control}
          required
          type="name"
          errors={errors}
        />
        <Input
          label={formFieldEmail}
          name="email"
          control={control}
          required
          type="email"
          errors={errors}
        />
        <Input label={formFieldEntityTitle} name="title" control={control} />
        <div className="text-sm font-bold text-zinc-700 ">
          <Altcha
            ref={altchaRef}
            style={{
              "--altcha-border-radius": "0.375rem",
              "--altcha-color-border": "rgb(212, 212, 216)",
              "--altcha-color-active": "teal",
              "--altcha-color-border-focus": "#2563eb",
              "--altcha-color-base": "rgba(255, 255, 255, 0.5)",
            }}
          />
        </div>
        <Switch
          label={subscribeHeaderSchool}
          name="somevahti"
          control={control}
        />
        <PrivacyPolicyLink text="" />

        <SubmitButton
          label={
            selectedReportIds.length > 1
              ? reportsDownloadButton
              : reportDownloadButton
          }
          isSubmitting={isSubmitting}
          isSubmitSuccessful={isSubmitSuccessful}
          isSubmitError={isSubmitError}
          errorMessage={`${errMessage} ${formErrorContactUs} ${localeConfig.contactEmail || ""}`}
        />
      </div>
    </form>
  )
}

const TrustedFlaggerSection = () => {
  const {
    trustedFlaggerTitle,
    trustedFlaggerDescription,
    trustedFlaggerReport2024,
    trustedFlaggerReport2025,
  } = useTranslations()

  const reports = [
    {
      title: trustedFlaggerReport2025,
      href: "https://s3.eu-central-1.amazonaws.com/files.someturva.fi/trusted-flagger-report-2025.pdf",
    },
    {
      title: trustedFlaggerReport2024,
      href: "https://s3.eu-central-1.amazonaws.com/files.someturva.fi/trusted-flagger-report-2024.pdf",
    },
  ]

  return (
    <div className="col-span-4 border-t border-zinc-700 pt-8">
      <h3 className="text-xl font-semibold text-zinc-300">
        {trustedFlaggerTitle}
      </h3>
      <p className="mt-2 max-w-3xl text-sm text-zinc-400">
        {trustedFlaggerDescription}
      </p>
      <div className="mt-4 flex flex-col gap-2">
        {reports.map((report, index) => (
          <a
            key={index}
            href={report.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {report.title} (PDF)
          </a>
        ))}
      </div>
    </div>
  )
}

export default function DownloadForm({ location = "blog" }) {
  const [showModal, setShowModal] = useState(false)
  const { webinarFormSuccessButton, reportsDownloadDescription, reportsHeroTitle, formSuccess } =
    useTranslations()
  return (
    <div
      className={cc([
        "relative bg-zinc-800",
        location === "page" ? "py-0 pb-10" : "py-8",
      ])}
      id="download-report"
    >
      <div className="mx-auto mb-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={cc([
            "max-w-4xl text-lg text-zinc-200",
            location === "page" ? "lg:py-4" : "lg:py-10",
          ])}
        >
          {location === "page" && reportsHeroTitle && (
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {reportsHeroTitle}
            </h2>
          )}
          <p
            className={cc([
              "max-w-4xl text-lg text-zinc-200",
              location === "page" ? "pt-6 lg:pt-4" : "pt-8",
            ])}
          >
            <span className="whitespace-pre-line text-zinc-100">
              {reportsDownloadDescription}
            </span>
          </p>
        </div>
        <div className="grid-cols-4 gap-8 space-y-8 transition duration-150 ease-in-out md:gap-10 lg:grid lg:grid-cols-5">
          <div className="col-span-4 mx-auto w-full">
            <SubscribeForm setShowModal={setShowModal} />
          </div>
          <TrustedFlaggerSection />
          <div className="space-between col-span-4 flex flex-col">
            <ContactInfoKaisa />
          </div>
        </div>
        <Modal
          isOpen={showModal}
          setIsOpen={setShowModal}
          title={formSuccess}
          bodyText=""
          buttonText={webinarFormSuccessButton}
        />
      </div>
    </div>
  )
}
